/**
 * Modal para gestionar incidencias de pedidos
 * Permite cambiar entre estados "incidencia" e "incidencia-resuelta"
 */
import React, { useState, useEffect } from 'react'
import { XMarkIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

interface Order {
  id: number
  orders_id: string
  customer_full_name?: string
  first_name?: string
  last_name?: string
  store_name: string
  total: string
  status: string
}

interface IncidenciaModalProps {
  isOpen: boolean
  onClose: () => void
  order: Order | null
  currentStatus: 'incidencia' | 'incidencia-resuelta'
  onConfirm: (texto: string) => void
  isLoading?: boolean
}

const IncidenciaModal: React.FC<IncidenciaModalProps> = ({
  isOpen,
  onClose,
  order,
  currentStatus,
  onConfirm,
  isLoading = false,
}) => {
  const [texto, setTexto] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Resetear el texto cuando cambia el estado o se abre/cierra el modal
  useEffect(() => {
    if (isOpen) {
      setTexto('')
      setError(null)
    }
  }, [isOpen, currentStatus])

  if (!isOpen || !order) return null

  const isResolving = currentStatus === 'incidencia'
  const label = isResolving ? 'Solución de la incidencia' : 'Motivo de la incidencia'
  const placeholder = isResolving
    ? 'Describe la solución aplicada para resolver la incidencia...'
    : 'Describe el motivo de la incidencia...'

  const handleConfirm = () => {
    // Validar que el texto no esté vacío
    if (!texto.trim()) {
      setError('Este campo es obligatorio')
      return
    }

    setError(null)
    onConfirm(texto.trim())
  }

  const handleClose = () => {
    if (!isLoading) {
      setTexto('')
      setError(null)
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              {isResolving ? (
                <CheckCircleIcon className="h-8 w-8 text-green-500" />
              ) : (
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {isResolving ? 'Resolver Incidencia' : 'Marcar como Incidencia'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              {isResolving
                ? 'Ingresa la solución aplicada para resolver esta incidencia:'
                : 'Ingresa el motivo por el cual se marca esta orden como incidencia:'}
            </p>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-4">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">ID de Orden:</span>
                <span className="text-gray-900">{order.orders_id}</span>
              </div>

              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Cliente:</span>
                <span className="text-gray-900">
                  {order.customer_full_name ||
                    `${order.first_name || ''} ${order.last_name || ''}`.trim() ||
                    'Sin nombre'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Tienda:</span>
                <span className="text-gray-900">{order.store_name}</span>
              </div>

              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Total:</span>
                <span className="text-gray-900 font-semibold">
                  $ {Number(order.total).toLocaleString()}
                </span>
              </div>
            </div>

            <div>
              <label
                htmlFor="incidencia-texto"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {label} <span className="text-red-500">*</span>
              </label>
              <textarea
                id="incidencia-texto"
                value={texto}
                onChange={(e) => {
                  setTexto(e.target.value)
                  if (error) setError(null)
                }}
                placeholder={placeholder}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  error ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading || !texto.trim()}
              className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 ${
                isResolving
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {isResolving ? 'Resolviendo...' : 'Marcando...'}
                </>
              ) : (
                isResolving ? 'Confirmar Resolución' : 'Confirmar Incidencia'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default IncidenciaModal

